import * as React from 'react';
import { capitalize } from 'lodash';

import DeliverableTypeCounters from 'modules/deliverables/components/deliverable-type-counters/DeliverableTypeCounters';
import CampaignHistoryOfferState from 'modules/influencers/components/history/campaign-history-offer-state/CampaignHistoryOfferState';
import CampaignEntityName from 'components/entity/entity-name/CampaignEntityName';
import ProfileEntityAvatar from 'components/entity/entity-avatar/ProfileEntityAvatar';
import Tooltip from 'components/Tooltip';

import { renderPrice } from 'utils/helper/price';
import { getDisplayDatesInterval } from 'utils/date';
import { __t, applyTranslations } from 'utils/language';
import { CHANNEL_TYPE_INSTAGRAM, CHANNEL_TYPE_TIKTOK, CHANNEL_TYPE_YOUTUBE } from 'utils/channels';

import './tile-standard-footer-history-item.scss';

import type { InfluencerCampaignHistoryItem } from 'types';

applyTranslations(require('../../tile-standard-footer.i18n.json'));

type Props = {
  item: InfluencerCampaignHistoryItem,
  disabled?: boolean
};

const MSG_KEY = 'tile_standard_footer';
const PLATFORM_SPLIT_CHANNELS = [CHANNEL_TYPE_INSTAGRAM, CHANNEL_TYPE_TIKTOK, CHANNEL_TYPE_YOUTUBE];

class TileStandardFooterHistoryItem extends React.PureComponent<Props> {
  isChannelSplit(): boolean {
    return !!PLATFORM_SPLIT_CHANNELS.map(channel => this.renderChannelWiseSplit(channel)).filter(jsx => jsx !== null).length;
  }

  renderChannelWiseSplit(channel: string): React.ReactNode {
    const { item } = this.props;
    const channelDeliverables = item.bindingAgreement.filter(task => task.type.includes(channel));
    const totalAmount = channelDeliverables.reduce((prevValue, task) => (prevValue + task.priceTotal), 0);
    const totalChannelDeliverables = channelDeliverables.length;

    if (totalAmount <= 0) {
      return null;
    }

    return (
      <React.Fragment key={channel}>
        <div className="tile-standard-footer-history-item__channel-platform-allocation">
          {`${capitalize(channel)}: ${totalAmount}€`}
        </div>
        {
          totalChannelDeliverables > 1 && (
            channelDeliverables.map((deliverable, index) => (
              <span className="tile-standard-footer-history-item__channel-task-type" key={deliverable.type}>
                {`${index === 0 ? '(' : ''}${__t(`${MSG_KEY}.labels.task_type.${deliverable.type}`)}: 
                  ${deliverable.priceTotal}€${index !== (totalChannelDeliverables - 1) ? ' | ' : ')'}`}
              </span>
            )))
        }
      </React.Fragment>
    );
  }

  render(): React.ReactNode {
    const { item, disabled } = this.props;
    const { campaign, deliverables, offer } = item;

    const isDraft = offer.state === 'draft';
    const brandProfile = campaign.brandProfile;
    const hasDeliverables = deliverables.length > 0;
    const hasChannelSplit = this.isChannelSplit();

    return (
      <div className="tile-standard-footer-history-item">
        <div className="tile-standard-footer-history-item__brand">
          <Tooltip
            message={brandProfile.name}
            width="short"
            display="flex"
          >
            <ProfileEntityAvatar
              profile={brandProfile}
              size="customSize"
              customSize={{
                width: 48,
                height: 48
              }}
              className="tile-standard-footer-history-item__logo"
            />
          </Tooltip>
        </div>
        <div className="tile-standard-footer-history-item__campaign">
          <div className="tile-standard-footer-history-item__head">
            <CampaignEntityName
              campaign={campaign}
              disabled={disabled}
              openInNewTab
            />
            <div className="tile-standard-footer-history-item__offer">
              <CampaignHistoryOfferState
                item={item}
              />
            </div>
          </div>
          {
            !isDraft && (
              <React.Fragment>
                <div className="tile-standard-footer-history-item__info">
                  <div className="tile-standard-footer-history-item__subset">
                    <span className="tile-standard-footer-history-item__label">
                      {__t(`${MSG_KEY}.labels.offer`)}:
                    </span>
                    <span className="tile-standard-footer-history-item__offer-type">
                      {__t(
                        `${MSG_KEY}.labels.offer_types.${offer.type}`,
                        { budget: renderPrice(offer.value, { separateThousands: false }) }
                      )}
                    </span>
                  </div>
                  <div className="tile-standard-footer-history-item__subset">
                    <span className="tile-standard-footer-history-item__label">
                      {__t(`${MSG_KEY}.labels.date`)}:
                    </span>
                    <span className="tile-standard-footer-history-item__date-value">
                      {getDisplayDatesInterval(campaign.startDate, campaign.endDate)}
                    </span>
                  </div>
                  <div className="tile-standard-footer-history-item__subset">
                    {
                      hasDeliverables ?
                        <DeliverableTypeCounters deliverables={deliverables} /> :
                        <span>-</span>
                    }
                  </div>
                </div>
                {
                  hasChannelSplit && (
                    <div className="tile-standard-footer-history-item__platform-allocation">
                      <span className="tile-standard-footer-history-item__platform-allocation-title">
                        {__t(`${MSG_KEY}.labels.platform_allocation`)}:
                      </span>
                      {
                        PLATFORM_SPLIT_CHANNELS.map(channel => this.renderChannelWiseSplit(channel))
                      }
                    </div>
                  )
                }
              </React.Fragment>
            )
          }
        </div>
      </div>
    );
  }
}

export default TileStandardFooterHistoryItem;
